(function registerEfPdfVote(globalScope) {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const API_DOMAIN = "https://api-a.ecoflow.com";
  const DETAIL_API_PATH = "/integral/web/rewardDraw/getRewardDrawById";
  const DRAW_API_PATH = "/integral/web/rewardDraw/subscribeDraw";
  const SUBSCRIPTION_API_PATH = "/website/subscribe/new";
  const DEFAULT_API_ORIGIN = "https://ca.ecoflow.com";
  const DEFAULT_API_REFERER = "https://ca.ecoflow.com/";
  const instances = new WeakMap();

  function trimValue(value) {
    return value == null ? "" : String(value).trim();
  }

  function normalizeEmail(value) {
    return String(value || "").trim().toLowerCase();
  }

  function isValidEmail(value) {
    return EMAIL_PATTERN.test(normalizeEmail(value));
  }

  function validateSubmission(state, messages) {
    if (!state.selectedPrize) return messages.prizeRequired;
    if (!state.selectedProduct) return messages.productRequired;
    if (!normalizeEmail(state.email)) return messages.emailRequired;
    if (!isValidEmail(state.email)) return messages.emailInvalid;
    if (!state.consent) return messages.consentRequired;
    return "";
  }

  function isSuccessfulResponse(data) {
    return String(data?.code ?? "") === "0" || data?.message === "Success";
  }

  function resolveApiError(data, messages) {
    if (String(data?.code) === "2433") return messages.duplicate;
    if (String(data?.code) === "1001") return messages.ipLimit;
    return messages.generic;
  }

  function getScrollState({ scrollLeft, clientWidth, scrollWidth }) {
    const tolerance = 2;
    return {
      canPrevious: scrollLeft > tolerance,
      canNext: scrollLeft + clientWidth < scrollWidth - tolerance,
    };
  }

  function joinUrl(domain, path) {
    const safeDomain = trimValue(domain).replace(/\/+$/, "");
    const safePath = trimValue(path);
    if (!safeDomain || !safePath) {
      return "";
    }

    return `${safeDomain}${safePath.charAt(0) === "/" ? "" : "/"}${safePath}`;
  }

  function buildDetailRequest(drawId) {
    return {
      url: joinUrl(API_DOMAIN, DETAIL_API_PATH),
      body: { id: trimValue(drawId) },
    };
  }

  function buildDrawRequest(drawId, version, email) {
    return {
      url: joinUrl(API_DOMAIN, DRAW_API_PATH),
      body: {
        drawId: trimValue(drawId),
        email: normalizeEmail(email),
        version: version,
      },
    };
  }

  function buildMultipleChoiceField(fieldId, optionValue) {
    const id = trimValue(fieldId);
    const value = trimValue(optionValue);
    if (!id || !value) {
      return null;
    }

    return {
      id,
      type: "multiple",
      value: [Number(value)],
      overwrite: false,
    };
  }

  function getResponsePayload(responseData) {
    return responseData?.data || responseData || {};
  }

  function resolveDrawVersion(rewardDrawInfo) {
    const version = rewardDrawInfo?.version;
    if (version == null) {
      return "";
    }

    if (typeof version === "number" && Number.isFinite(version)) {
      return version;
    }

    return trimValue(version);
  }

  function isMissingDrawVersion(version) {
    return version === "" || version == null;
  }

  const core = {
    normalizeEmail,
    isValidEmail,
    validateSubmission,
    isSuccessfulResponse,
    resolveApiError,
    getScrollState,
    buildDetailRequest,
    buildDrawRequest,
    buildMultipleChoiceField,
  };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = core;
  }

  if (globalScope) {
    globalScope.EfPdfVoteCore = core;
  }

  if (typeof document === "undefined") {
    return;
  }

  function parseConfig(root) {
    const configNode = root.querySelector("[data-pdf-vote-config]");
    if (!configNode) {
      return {};
    }

    try {
      return JSON.parse(configNode.textContent || "{}");
    } catch (error) {
      console.warn("[ef-pdf-vote] Failed to parse section config.", error);
      return {};
    }
  }

  function getEmailSubscriptionConfig() {
    return (typeof window !== "undefined" && window.theme?.emailSubscription) || null;
  }

  async function postJson({ url, body, headers, label }) {
    if (!url) {
      throw new Error(`${label} api is missing.`);
    }

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    });

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const error = new Error(`${label} request failed with status ${response.status}`);
      error.responseData = data;
      throw error;
    }

    return data;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  async function waitForSubscriptionHelpers(maxWaitMs) {
    const startedAt = Date.now();

    while (Date.now() - startedAt < maxWaitMs) {
      if (typeof getContactListId === "function") {
        return true;
      }

      await wait(50);
    }

    return typeof getContactListId === "function";
  }

  function createController(root) {
    const config = parseConfig(root);
    const settings = config.settings || {};
    const messages = config.messages || {};
    const form = root.querySelector("[data-vote-form]");
    const emailInput = root.querySelector("[data-vote-email]");
    const consentInput = root.querySelector("[data-vote-consent]");
    const submitButton = root.querySelector("[data-vote-submit]");
    const errorNode = root.querySelector("[data-vote-error]");
    const successNode = root.querySelector("[data-vote-success]");
    const prizeCards = Array.from(root.querySelectorAll('[data-vote-card="prize"]'));
    const productCards = Array.from(root.querySelectorAll('[data-vote-card="product"]'));
    const tracks = Array.from(root.querySelectorAll("[data-vote-track]"));
    const listeners = [];

    const state = {
      selectedPrize: "",
      selectedProduct: "",
      email: "",
      consent: Boolean(consentInput && consentInput.checked),
      loading: false,
      success: false,
      error: "",
      rewardDrawInfo: null,
      rewardDrawVersion: "",
    };

    function addListener(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push({ target, type, handler, options });
    }

    function setError(message) {
      state.error = message || "";
      if (!errorNode) {
        return;
      }

      errorNode.textContent = state.error;
      errorNode.hidden = state.error === "";
    }

    function setLoading(isLoading) {
      state.loading = isLoading;
      root.classList.toggle("is-loading", isLoading);
      if (submitButton) {
        submitButton.disabled = isLoading || !trimValue(settings.draw_id) || isMissingDrawVersion(state.rewardDrawVersion);
        const label = isLoading
          ? settings.loading_text || "Loading..."
          : settings.button_label || "Submit";
        const labelNode = submitButton.querySelector("[data-vote-submit-label]");
        if (labelNode) {
          labelNode.textContent = label;
        } else {
          submitButton.textContent = label;
        }
      }
    }

    function setSuccess() {
      state.success = true;
      root.classList.add("is-success");
      if (successNode) {
        successNode.hidden = false;
      }
      setError("");
    }

    function syncCardSelection(cards, selectedId) {
      cards.forEach((card) => {
        const input = card.querySelector('input[type="radio"]');
        const isSelected = input && input.value === selectedId && input.checked;
        card.classList.toggle("is-selected", Boolean(isSelected));
      });
    }

    function readSelections() {
      const prizeInput = root.querySelector('input[name^="pdf-vote-prize-"]:checked');
      const productInput = root.querySelector('input[name^="pdf-vote-product-"]:checked');
      state.selectedPrize = prizeInput ? prizeInput.value : "";
      state.selectedProduct = productInput ? productInput.value : "";
      state.email = emailInput ? emailInput.value : "";
      state.consent = Boolean(consentInput && consentInput.checked);
      syncCardSelection(prizeCards, state.selectedPrize);
      syncCardSelection(productCards, state.selectedProduct);
    }

    function updateTrackControls(track) {
      const step = track.closest("[data-vote-step]");
      const previous = step?.querySelector("[data-vote-previous]");
      const next = step?.querySelector("[data-vote-next]");
      if (!previous || !next) {
        return;
      }

      const scrollState = getScrollState({
        scrollLeft: track.scrollLeft,
        clientWidth: track.clientWidth,
        scrollWidth: track.scrollWidth,
      });

      previous.disabled = !scrollState.canPrevious;
      next.disabled = !scrollState.canNext;
    }

    function bindTrack(track) {
      const step = track.closest("[data-vote-step]");
      const previous = step?.querySelector("[data-vote-previous]");
      const next = step?.querySelector("[data-vote-next]");
      const card = track.querySelector("[data-vote-card]");
      const gap = 16;
      const amount = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;

      const onScroll = function () {
        updateTrackControls(track);
      };

      addListener(track, "scroll", onScroll, { passive: true });

      if (previous) {
        addListener(previous, "click", function () {
          track.scrollBy({ left: -amount, behavior: "smooth" });
        });
      }

      if (next) {
        addListener(next, "click", function () {
          track.scrollBy({ left: amount, behavior: "smooth" });
        });
      }

      updateTrackControls(track);
    }

    async function loadRewardDrawInfo() {
      const drawId = trimValue(settings.draw_id);
      if (!drawId) {
        state.rewardDrawInfo = null;
        state.rewardDrawVersion = "";
        setLoading(false);
        setError(messages.missingConfig || messages.generic || "Missing lottery configuration.");
        return;
      }

      try {
        const request = buildDetailRequest(drawId);
        const data = await postJson({
          url: request.url,
          body: request.body,
          headers: {
            "Content-Type": "application/json",
            "x-appid": "1",
            Origin: DEFAULT_API_ORIGIN,
            Referer: DEFAULT_API_REFERER,
          },
          label: "rewardDrawDetail",
        });

        if (!isSuccessfulResponse(data)) {
          throw Object.assign(new Error("rewardDrawDetail request was not successful."), {
            responseData: data,
          });
        }

        const payload = getResponsePayload(data);
        state.rewardDrawInfo = Array.isArray(payload)
          ? payload[0] || null
          : payload && typeof payload === "object"
            ? payload
            : null;
        state.rewardDrawVersion = resolveDrawVersion(state.rewardDrawInfo);

        if (isMissingDrawVersion(state.rewardDrawVersion)) {
          setError(messages.missingConfig || messages.generic || "Missing lottery configuration.");
        }
      } catch (error) {
        state.rewardDrawInfo = null;
        state.rewardDrawVersion = "";
        setError(messages.generic || "Please try again.");
        console.warn("[ef-pdf-vote] Reward draw detail request failed.", error);
      } finally {
        setLoading(false);
      }
    }

    function getSelectedCardLabel(cardType) {
      const checked = root.querySelector(
        `[data-vote-card="${cardType}"] input:checked`
      );
      if (!checked) {
        return "";
      }

      const card = checked.closest("[data-vote-card]");
      const label = card?.querySelector(".ef-pdf-vote__caption, .ef-pdf-vote__name");
      return trimValue(label?.textContent).replace(/\s+/g, " ");
    }

    function buildSubscribeTrackingContext(status) {
      const buttonName =
        trimValue(submitButton?.querySelector("[data-vote-submit-label]")?.textContent) ||
        trimValue(submitButton?.textContent) ||
        "submit";
      const productName = [getSelectedCardLabel("prize"), getSelectedCardLabel("product")]
        .filter(Boolean)
        .join("_");
      const moduleName =
        trimValue(settings.section_id) ||
        trimValue(root.closest(".star-page-container")?.id) ||
        "ef-pdf-vote";

      return {
        trigger: submitButton,
        root,
        buttonName,
        status: status || "success",
        consentPolicy: state.consent ? "Agree" : "Disagree",
        productName,
        moduleName,
        entryName: buttonName,
        entryPosition: "none",
        entryType: "fixed entry",
        ts: Date.now(),
      };
    }

    function trackSubscribeEvent(eventName, status) {
      const push =
        typeof window.pushSubscribeUaEvent === "function"
          ? window.pushSubscribeUaEvent
          : typeof window.pushSubscribeGa4Event === "function"
            ? window.pushSubscribeGa4Event
            : null;

      if (!push) {
        return null;
      }

      const context = buildSubscribeTrackingContext(status);
      if (eventName === "click_subscribe") {
        window.__ecoflowSubscribeLastTrackingContext = context;
      }

      push(eventName, context);
      return context;
    }

    async function requestEmailSubscription(email) {
      const helpersReady = await waitForSubscriptionHelpers(5000);
      if (!helpersReady) {
        throw new Error("subscription-form-common.js is not loaded.");
      }

      const contactListId = getContactListId(settings);
      const subscriptionConfig = getEmailSubscriptionConfig();

      if (!trimValue(contactListId)) {
        throw new Error("Email subscription contact_list_id is missing in section settings.");
      }

      if (!trimValue(subscriptionConfig?.apiKey) || !trimValue(subscriptionConfig?.xAppid)) {
        throw new Error("Email subscription apiKey or xAppid is missing in theme settings.");
      }

      const fields = typeof createFieldsArray === "function" ? createFieldsArray(settings) : [];
      const prizeField = buildMultipleChoiceField(
        settings.prize_option_id,
        state.selectedPrize
      );
      if (prizeField) {
        fields.push(prizeField);
      }
      const productField = buildMultipleChoiceField(
        settings.product_option_id,
        state.selectedProduct
      );
      if (productField) {
        fields.push(productField);
      }
      const subscribeInfoCookie = window.getCookieForEmarsys?.("subscribe_info");
      let eventTracking = null;

      if (subscribeInfoCookie) {
        try {
          eventTracking = JSON.parse(decodeURIComponent(subscribeInfoCookie));
          delete eventTracking.ts;
        } catch (error) {
          console.warn("[ef-pdf-vote] Failed to parse subscribe_info cookie.", error);
        }
      }

      const traceId =
        window.traceId ||
        (typeof createTraceId === "function" ? createTraceId() : "");

      // subscribe_success is emitted by subscription-form-common.js fetch hook.
      const response = await fetch(joinUrl(API_DOMAIN, SUBSCRIPTION_API_PATH), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apiKey: subscriptionConfig.apiKey,
          traceId,
          "X-appid": subscriptionConfig.xAppid,
          "Accepted-Language": subscriptionConfig.acceptedLanguage,
        },
        body: JSON.stringify({
          email,
          contactListId,
          fields,
          ...(eventTracking && { eventTracking }),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.message !== "Success") {
        throw new Error(data?.message || "Email subscription request failed.");
      }

      return data;
    }

    async function submitForm(event) {
      event.preventDefault();
      if (state.loading || state.success) {
        return;
      }

      readSelections();
      const validationError = validateSubmission(state, messages);
      if (validationError) {
        trackSubscribeEvent("click_subscribe", "fail");
        setError(validationError);
        if (!state.email && emailInput) {
          emailInput.focus();
        }
        return;
      }

      if (!trimValue(settings.draw_id) || isMissingDrawVersion(state.rewardDrawVersion)) {
        trackSubscribeEvent("click_subscribe", "fail");
        setError(messages.missingConfig || messages.generic || "Missing lottery configuration.");
        return;
      }

      trackSubscribeEvent("click_subscribe", "success");
      setLoading(true);
      setError("");

      const request = buildDrawRequest(
        settings.draw_id,
        state.rewardDrawVersion,
        state.email
      );

      try {
        const data = await postJson({
          url: request.url,
          body: request.body,
          headers: {
            "Content-Type": "application/json",
            "x-appid": "1",
            Origin: DEFAULT_API_ORIGIN,
            Referer: DEFAULT_API_REFERER,
          },
          label: "subscribeDraw",
        });

        if (!isSuccessfulResponse(data)) {
          setError(resolveApiError(data, messages));
          return;
        }

        await requestEmailSubscription(request.body.email);
        setSuccess();
      } catch (error) {
        setError(resolveApiError(error.responseData, messages));
        console.warn("[ef-pdf-vote] submit failed.", error);
      } finally {
        setLoading(false);
      }
    }

    prizeCards.concat(productCards).forEach((card) => {
      addListener(card, "change", readSelections);
      addListener(card, "click", function (event) {
        const input = card.querySelector('input[type="radio"]');
        if (!input || event.target === input) {
          return;
        }
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    if (emailInput) {
      addListener(emailInput, "input", function () {
        state.email = emailInput.value;
      });
    }

    if (consentInput) {
      addListener(consentInput, "change", function () {
        state.consent = consentInput.checked;
      });
    }

    if (form) {
      addListener(form, "submit", submitForm);
    }

    tracks.forEach(bindTrack);

    const onResize = function () {
      tracks.forEach(updateTrackControls);
    };
    addListener(window, "resize", onResize);

    setLoading(false);
    loadRewardDrawInfo();

    return {
      destroy: function () {
        listeners.forEach(function (item) {
          item.target.removeEventListener(item.type, item.handler, item.options);
        });
      },
    };
  }

  function mount(root) {
    if (!root || instances.has(root)) {
      return;
    }

    instances.set(root, createController(root));
  }

  function unmount(root) {
    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(root);
  }

  function mountAll(scope) {
    (scope || document)
      .querySelectorAll("[data-pdf-vote-section]")
      .forEach(mount);
  }

  document.addEventListener("DOMContentLoaded", function () {
    mountAll(document);
  });

  document.addEventListener("shopify:section:load", function (event) {
    mountAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    event.target.querySelectorAll("[data-pdf-vote-section]").forEach(unmount);
  });

  if (document.readyState !== "loading") {
    mountAll(document);
  }

  if (globalScope) {
    globalScope.EfPdfVoteSection = {
      mount: mount,
      unmount: unmount,
    };
  }
})(typeof window !== "undefined" ? window : globalThis);
